/**
 * Thrivia Embed SDK: the typed postMessage bus.
 *
 * Purpose: the ONE postMessage surface in the whole bundle. Nothing else in `src/embed-loader/`
 * may call `postMessage` or add a `message` listener, because every cross-document channel is a
 * security boundary and one boundary is auditable where five are not.
 *
 * THE WIRE CONTRACT (this file is the authority; Task 7's frames are written against it):
 *
 *   { ns: "thrivia", v: 1, type: <BusType>, payload: <object> }
 *
 * - `ns` and `v` are BOTH checked on receipt. The namespace keeps us out of the way of every other
 *   widget on an organizer's page (Google, YouTube, Intercom and a live-chat widget all broadcast
 *   into the same `window`), and the version is what lets a v2 loader ignore a v1 frame instead of
 *   misreading its payload.
 * - `payload` is always an object, never a bare value, so a field can be added without changing
 *   the shape a receiver destructures.
 *
 * ORIGIN DISCIPLINE, and it is the whole point of the file:
 *
 * - Outbound: every post targets {@link initBus}'s origin, which is the origin the LOADER ITSELF
 *   was served from (`config.origin`, derived from our own `<script src>`). Never `'*'`. A `'*'`
 *   post is readable by whatever document happens to occupy the frame, which on an organizer's
 *   page is not necessarily the document we put there.
 * - Inbound: `event.origin` must equal that same origin, exactly. Anything else returns before the
 *   payload is even looked at. An organizer's page is full of other posters and one of them will
 *   eventually send `{type:"booking:complete"}` by accident or on purpose.
 * - Everything that fails a check is dropped SILENTLY. No console noise on somebody else's site,
 *   and no oracle for a page probing what we accept.
 *
 * Message types (both directions use the same vocabulary):
 *
 * | type               | direction      | payload                                        |
 * |--------------------|----------------|------------------------------------------------|
 * | `resize`           | frame -> loader| `{ height: number }`                            |
 * | `viewport`         | loader -> frame| `{ top: number, height: number }`               |
 * | `checkout:close`   | both           | `{}`                                            |
 * | `login:open`       | frame -> loader| `{}`                                            |
 * | `login:complete`   | popup -> loader| `{}` (relayed loader -> checkout frame)         |
 * | `booking:complete` | frame -> loader| `{ eventId, bookingId, value, currency }`       |
 * | `checkout:handoff` | frame -> loader| `{ url }` (JioPay page; loader re-validates)    |
 * | `error`            | frame -> loader| `{ message?: string }`                          |
 *
 * A token, a session cookie value or anything identifying a guest must never ride on this bus: it
 * crosses into a document we do not control the moment it leaves.
 *
 * No runtime dependencies, DOM only, ES2019 output target.
 */

/** Namespace on every message. Anything else on the wire is somebody else's widget. */
export const NS = "thrivia";

/** Protocol version. Bumped only on a breaking change to a payload shape. */
export const V = 1;

/**
 * `viewport` is the only message that goes loader -> frame carrying geometry, and it exists
 * because a page mount grows its frame to the height of its content. `position: fixed` inside that
 * frame resolves against the FRAME's box, not the visitor's window, so a dialog opened at the
 * bottom of a 4000px mount paints at the frame's centre — 2000px above or below anything the
 * visitor can see. The frame cannot work out where the visitor is looking (it cannot read the
 * parent's scroll position across origins), and the loader cannot reach inside the frame, so the
 * loader measures and says. `top` and `height` are the visible band expressed in the FRAME's own
 * coordinates, which is the space the frame's CSS is written in.
 *
 * It carries geometry and nothing else: the numbers describe the organizer's window, never their
 * visitor.
 */
export type BusType =
  | "resize"
  | "viewport"
  | "checkout:close"
  | "login:open"
  | "login:complete"
  | "booking:complete"
  | "checkout:handoff"
  | "error";

export interface BusPayload {
  [key: string]: unknown;
}

/**
 * `source` is the window the message came from, passed straight through from the `MessageEvent`.
 *
 * The bus validates the ORIGIN; only a subscriber knows which document it is expecting. Every
 * `/embed/*` page answers from the same origin, so origin alone cannot tell the checkout frame from
 * the login popup, and Task 7's page surface will need it again to match a `resize` to the right
 * one of several frames.
 */
export type BusHandler = (
  type: BusType,
  payload: BusPayload,
  source: MessageEventSource | null,
) => void;

/**
 * The one origin this bus talks to and listens to, set once at boot from the loader's own script
 * origin. Empty until then, and an empty origin posts nothing and accepts nothing: a bus with no
 * origin has no way to tell our frame from any other document and must stay shut.
 */
let appOrigin = "";

const handlers: BusHandler[] = [];
let listening = false;

/** Called once at boot with `config.origin`. */
export function initBus(origin: string): void {
  appOrigin = origin;
}

/**
 * Posts one message to a document we opened.
 *
 * The second argument to `postMessage` is the targetOrigin and it is a REQUIREMENT, not a hint:
 * the browser refuses to deliver unless the receiving document's origin matches, so a navigated or
 * replaced frame silently gets nothing instead of getting our payload.
 */
export function post(target: Window | null, type: BusType, payload?: BusPayload): void {
  try {
    if (!target || !appOrigin) return;
    target.postMessage({ ns: NS, v: V, type: type, payload: payload || {} }, appOrigin);
  } catch {
    /* a closed popup, a detached frame, a host that froze postMessage */
  }
}

/** The single `message` listener. Every check that fails drops the message without a word. */
function receive(event: MessageEvent): void {
  try {
    if (!appOrigin || event.origin !== appOrigin) return;
    const data = event.data as { ns?: unknown; v?: unknown; type?: unknown; payload?: unknown };
    if (!data || typeof data !== "object") return;
    if (data.ns !== NS || data.v !== V || typeof data.type !== "string") return;
    const payload =
      data.payload && typeof data.payload === "object" ? (data.payload as BusPayload) : {};
    // Length read once: a handler that subscribes during dispatch must not run in this same pass.
    // The try is INSIDE the loop, not around it: these handlers are independent subscribers, and
    // one of them throwing used to abandon every handler after it - so a failure in the analytics
    // re-dispatch silently stopped the overlay from ever seeing `checkout:close`.
    for (let i = 0, n = handlers.length; i < n; i++) {
      try {
        handlers[i](data.type as BusType, payload, event.source);
      } catch {
        /* guest code: a throwing handler must not become an error event on the organizer's page */
      }
    }
  } catch {
    /* a malformed event that throws before dispatch even starts */
  }
}

/**
 * Subscribes to validated inbound messages.
 *
 * One window listener however many subscribers there are, attached on the first subscription
 * rather than at boot, so a page that never opens a checkout never listens at all.
 */
export function onBus(handler: BusHandler): void {
  handlers.push(handler);
  if (listening) return;
  listening = true;
  window.addEventListener("message", receive);
}

/**
 * Re-dispatches `booking:complete` as a DOM event the organizer's own tags can listen for.
 *
 * `window.addEventListener("thrivia:booking:complete", e => gtag("event","purchase", e.detail))` is
 * the whole integration, which is why the four fields are picked out ONE BY ONE rather than spread
 * from the payload. Spreading would put whatever the checkout frame happens to send that month into
 * an organizer's GA4 property and their Meta Pixel: a guest email, a phone number or a name reaching
 * a third-party analytics vendor is a data-protection incident that no code review of this file
 * would ever catch, because the leak would be written in the other repository.
 *
 * `undefined` for a field the frame omitted is deliberate: the key stays, so a consumer reading
 * `detail.value` never has to guard for a missing property shape.
 */
export function emitBooking(payload: BusPayload): void {
  // No try/catch of its own: every caller reaches this through `receive`, which already swallows
  // everything, so an engine without `CustomEvent` (or an organizer listener that throws) costs
  // them their analytics hook and never their page.
  window.dispatchEvent(
    new CustomEvent("thrivia:booking:complete", {
      detail: {
        eventId: payload.eventId,
        bookingId: payload.bookingId,
        value: payload.value,
        currency: payload.currency,
      },
    }),
  );
}
